<template>
    <div id="workstation-screen-form-page">
        <div
            class="workstation-screen-form"
            v-if="formType"
        >
            <div class="top-buttons">
                <ui5-button @click="back()">
                    {{
                        trans('pages.workstation_screen_form.back_to_overview')
                    }}
                </ui5-button>
            </div>

            <loader
                v-if="loading"
                :cover="true"
            />

            <form>
                <div class="field">
                    <ui5-label required>
                        {{ trans('pages.workstation_screen_form.workstation') }}
                    </ui5-label>
                    <auto-complete-input
                        v-if="sortedWorkstations && sortedWorkstations.length"
                        :disabled="formType !== 'add'"
                        ref="workstationSelector"
                        :placeholder="
                            trans(
                                'pages.workstation_screen_form.select_workstation'
                            )
                        "
                        v-model="workstationId"
                        :options="sortedWorkstations"
                        @input="fetchProductTypes()"
                        :initial-search="
                            getLabel(workstationId, sortedWorkstations)
                        "
                        :lbl-no-options="
                            trans(
                                'pages.workstation_screen_form.no_workstations_found'
                            )
                        "
                    />
                    <div
                        v-if="getErrorFromValidator($v.workstationId).length"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.workstationId) }}
                    </div>
                </div>

                <div class="field">
                    <ui5-label required>
                        {{
                            trans('pages.workstation_screen_form.product_type')
                        }}
                    </ui5-label>
                    <auto-complete-input
                        :disabled="formType !== 'add'"
                        ref="productTypeSelector"
                        :placeholder="
                            trans(
                                'pages.workstation_screen_form.select_product_type'
                            )
                        "
                        v-model="producttypeId"
                        :options="sortedProductTypes"
                        :lbl-no-options="
                            trans(
                                'pages.workstation_screen_form.no_product_types_found'
                            )
                        "
                    />
                    <div
                        v-if="getErrorFromValidator($v.producttypeId).length"
                        class="error"
                    >
                        {{ getErrorFromValidator($v.producttypeId) }}
                    </div>
                </div>

                <template v-if="formType === 'edit'">
                    <div class="field">
                        <ui5-label>
                            {{
                                trans(
                                    'pages.workstation_screen_form.background'
                                )
                            }}
                        </ui5-label>
                        <ui5-file-uploader
                            @change="fileAdded"
                            @click="fileClick"
                            :value="imageFilename"
                            :accept="ALLOWED_FILE_TYPES.join(',')"
                        >
                            <ui5-button>
                                {{
                                    trans(
                                        'pages.workstation_screen_form.upload_file'
                                    )
                                }}
                            </ui5-button>
                        </ui5-file-uploader>
                        <i
                            class="fa fa-trash"
                            v-if="backgroundImage"
                            @click="
                                imageFilename = '';
                                backgroundImage = null;
                                backgroundFile = null;
                                removeBackground = true;
                            "
                        />
                        <i
                            class="fa fa-trash-restore"
                            v-if="originalBackgroundImage && removeBackground"
                            @click="
                                isNewBackground = false;
                                imageFilename = originalImageFilename;
                                backgroundImage = originalBackgroundImage;
                                removeBackground = false;
                            "
                        />
                    </div>
                    <div>
                        <WorkstationScreenEditor
                            :datafields="datafields"
                            :sawlines="sawlines"
                            :background-image="backgroundImage"
                            v-model="screenItems"
                        />
                    </div>
                </template>

                <div class="button-container">
                    <ui5-button
                        @click="save"
                        :disabled="saving"
                    >
                        <spinner v-if="saving" />
                        {{ trans('pages.workstation_screen_form.save') }}
                    </ui5-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import workstationApi from '../api/workstation';
import productTypeApi from '../api/productType';
import workstationScreenApi from '../api/workstationScreen';
import Loader from '../components/loader/Loader';
import Spinner from '../components/loader/Spinner';
import AutoCompleteInput from '../components/AutoCompleteInput';
import WorkstationScreenEditor from '../components/workstationScreens/WorkstationScreenEditor';
import {
    TYPE_DATAFIELD,
    TYPE_SAWLINE,
    TYPE_SPECIAL,
} from '../shared/workstationScreens';
import IdParamMixin from '../mixins/id-param';
import { getImageSize, getAspectRatio } from '../helpers/FileHelper';
import ErrorHelper from '../helpers/ErrorHelper';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import _ from 'lodash';
import { getLabel } from '../shared/collections';
import localization from '../mixins/localization';

const ALLOWED_RATIO = '16:9';
const ALLOWED_FILE_TYPES = ['.png', '.jpg', '.jpeg', '.gif'];

let lastFilename = '';

export default {
    mixins: [IdParamMixin(), localization],
    computed: {
        ALLOWED_FILE_TYPES: () => ALLOWED_FILE_TYPES,
        sortedWorkstations() {
            return _.sortBy(this.workstations, (x) => x.label).map((x) => ({
                value: x.id,
                label: x.label,
            }));
        },
        sortedProductTypes() {
            return [
                { label: '---', value: null },
                ..._.sortBy(this.producttypes, (x) => x.label).map((x) => {
                    return { label: x.label, value: x.producttypeId };
                }),
            ];
        },
    },
    data() {
        return {
            saving: false,
            formType: null,
            loading: false,
            id: null,
            workstationId: null,
            producttypeId: null,
            producttypeLabel: null,
            isNewBackground: false,
            removeBackground: false,
            backgroundFile: null,
            originalBackgroundImage: null,
            backgroundImage: null,
            screenItems: [],
            workstations: [],
            producttypes: [],
            datafields: [],
            sawlines: [],
            originalImageFilename: '',
            imageFilename: '',
            maxFileSize: null,
            maxFileSizeRaw: null,
        };
    },
    validations() {
        return {
            id: {},
            workstationId: {
                required: required,
            },
            producttypeId: {
                required: required,
            },
        };
    },
    methods: {
        getLabel,
        load() {
            this.loading = true;
            const promises = [];

            this.formType =
                this.$route.params && this.$route.params.id ? 'edit' : 'add';

            if (this.formType === 'add') {
                this.workstationId = this.$route.query.workstationId;
                promises.push(this.fetchProductTypes());
            } else {
                promises.push(
                    workstationScreenApi
                        .fetchById(this.$route.params.id)
                        .then((response) => {
                            const screen = response.data.data;
                            this.id = screen.id;
                            this.workstationId = screen.workstationId;
                            this.producttypeLabel = screen.producttypeLabel;
                            this.producttypeId = screen.producttypeId;
                            this.maxFileSize = screen.maxFileSize;
                            this.maxFileSizeRaw = screen.maxFileSizeRaw;
                            this.screenItems = [
                                ...(screen.datafields
                                    ? screen.datafields.map((x) => ({
                                          ...x,
                                          type: TYPE_DATAFIELD,
                                          selected: false,
                                      }))
                                    : []),
                                ...(screen.sawlines
                                    ? screen.sawlines.map((x) => ({
                                          ...x,
                                          type: TYPE_SAWLINE,
                                          selected: false,
                                      }))
                                    : []),
                                ...(screen.specials
                                    ? screen.specials.map((x) => ({
                                          ...x,
                                          type: TYPE_SPECIAL,
                                          selected: false,
                                      }))
                                    : []),
                            ];
                            if (screen.backgroundImageUrl) {
                                const image = new window.Image();
                                image.onerror = () => {
                                    showErrorMessage(
                                        this.trans(
                                            'pages.workstation_screen_form.invalid_background_image'
                                        )
                                    );
                                    this.originalBackgroundImage =
                                        this.backgroundImage = null;
                                    this.originalImageFilename =
                                        this.imageFilename = '';
                                };
                                image.onload = () => {
                                    this.originalBackgroundImage =
                                        this.backgroundImage = image;
                                    this.originalImageFilename =
                                        this.imageFilename =
                                            screen.backgroundImagePath;
                                };
                                image.src = screen.backgroundImageUrl;
                            }
                            this.$forceUpdate();
                            return this.fetchProductTypes();
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.workstation_screen_form.unknown_error_fetching_workstation_screen'
                                )
                            );
                        })
                );
            }

            promises.push(
                workstationApi
                    .fetchAllWorkstations()
                    .then((response) => {
                        this.workstations = Object.values(
                            response.data.data
                        ).reduce(
                            (workstations, data) => workstations.concat(data),
                            []
                        );
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.workstation_screen_form.unknown_error_fetching_workstation_types'
                            )
                        );
                    })
            );

            Promise.all(promises).then(() => {
                this.loading = false;
            });
        },
        fileClick($event) {
            lastFilename = $event.target.value;
        },
        fileAdded($event) {
            const file = $event.target.files[0];

            if (!file) {
                $event.target.value = lastFilename;
                return;
            }

            const extension = file.name.split('.').pop();
            if (
                !ALLOWED_FILE_TYPES.map((x) => x.substr(1)).includes(extension)
            ) {
                $event.target.value = lastFilename;
                return showErrorMessage(
                    this.trans(
                        'pages.workstation_screen_form.background_file_type_error',
                        { types: ALLOWED_FILE_TYPES.join(', ') }
                    )
                );
            }

            if (file.size > this.maxFileSizeRaw) {
                return showErrorMessage(
                    this.trans(
                        'pages.workstation_screen_form.background_file_size_error',
                        { size: this.maxFileSize }
                    )
                );
            }

            getImageSize(file)
                .then((size) => {
                    const aspectRatio = getAspectRatio(size.width, size.height);
                    if (aspectRatio != ALLOWED_RATIO) {
                        $event.target.value = lastFilename;
                        return showErrorMessage(
                            this.trans(
                                'pages.workstation_screen_form.background_aspect_ratio_error',
                                { required: ALLOWED_RATIO, actual: aspectRatio }
                            )
                        );
                    }
                    this.backgroundFile = file;
                    this.backgroundImage = size.image;
                    this.isNewBackground = true;
                    this.imageFilename = file.name;
                })
                .catch(() => {
                    $event.target.value = lastFilename;
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_screen_form.invalid_image_file'
                        )
                    );
                });
        },
        back() {
            this.$router.push('/settingsworkstationtypes');
        },
        translateErrorCode(errorCode) {
            const errorMessages = {
                'workstationScreen.noExists': this.trans(
                    'pages.workstation_screen_form.errors.workstation_screen_not_exists'
                ),
                'backgroundImage.fileSize': this.trans(
                    'pages.workstation_screen_form.errors.file_size_too_large',
                    { maxSize: this.maxFileSize }
                ),
                'backgroundImage.removeFail': this.trans(
                    'pages.workstation_screen_form.errors.background_remove_fail'
                ),
                'backgroundImage.uploadFail': this.trans(
                    'pages.workstation_screen_form.errors.background_upload_fail'
                ),
                'backgroundImage.saveFail': this.trans(
                    'pages.workstation_screen_form.errors.background_save_fail'
                ),
                'workstationId empty': this.trans(
                    'pages.workstation_screen_form.errors.workstation_id_empty'
                ),
                'id empty': this.trans(
                    'pages.workstation_screen_form.errors.id_empty'
                ),
                'upload fail': this.trans(
                    'pages.workstation_screen_form.errors.upload_fail'
                ),
                'backgroundImage.notImage': this.trans(
                    'pages.workstation_screen_form.errors.background_not_image'
                ),
                'backgroundImage.not169': this.trans(
                    'pages.workstation_screen_form.errors.background_not_16_9'
                ),
                'backgroundImage.invalidMime': this.trans(
                    'pages.workstation_screen_form.errors.background_invalid_mime'
                ),
                'datafield.datafieldId empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_id_empty'
                ),
                'datafield.top empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_top_empty'
                ),
                'datafield.left empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_left_empty'
                ),
                'datafield.showCheckbox empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_show_checkbox_empty'
                ),
                'datafield.fontSize empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_font_size_empty'
                ),
                'datafield.fontStyle empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_font_style_empty'
                ),
                'datafield.color empty': this.trans(
                    'pages.workstation_screen_form.errors.datafield_color_empty'
                ),
                'sawline.sawlineId empty': this.trans(
                    'pages.workstation_screen_form.errors.sawline_id_empty'
                ),
                'sawline.top empty': this.trans(
                    'pages.workstation_screen_form.errors.sawline_top_empty'
                ),
                'sawline.left empty': this.trans(
                    'pages.workstation_screen_form.errors.sawline_left_empty'
                ),
                'sawline.showCheckbox empty': this.trans(
                    'pages.workstation_screen_form.errors.sawline_show_checkbox_empty'
                ),
            };

            return (
                errorMessages[errorCode] ||
                this.trans(
                    'pages.workstation_screen_form.errors.unknown_save_error'
                )
            );
        },
        save() {
            let valid = true;
            [this, ...Object.values(this.$refs).flat()].forEach((x) => {
                if (x && x.$v) {
                    x.$v.$touch();
                    if (x.$v.$invalid) {
                        valid = false;
                    }
                }
            });

            if (!valid) {
                showErrorMessage(
                    this.trans(
                        'pages.workstation_screen_form.resolve_input_errors'
                    )
                );
                return;
            }

            if (this.formType === 'add') {
                const payload = {
                    id: this.id,
                    workstationId: this.workstationId,
                    producttypeId: this.producttypeId,
                };

                this.saving = true;
                workstationScreenApi
                    .createWorkstationScreen(payload)
                    .then((response) => {
                        showInfoMessage(
                            this.trans(
                                'pages.workstation_screen_form.workstation_screen_saved'
                            )
                        );
                        const id = response.data.data;
                        this.$router.push('/editworkstationscreen/' + id);
                    })
                    .catch((error) => {
                        const errorCode = ErrorHelper.parseError(
                            error.response.data
                        );
                        showErrorMessage(this.translateErrorCode(errorCode));
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            } else if (this.formType === 'edit') {
                const payload = {
                    id: this.id,
                    backgroundFile:
                        this.isNewBackground && !this.removeBackground
                            ? this.backgroundFile
                            : null,
                    removeBackground: this.removeBackground,
                    datafields: JSON.stringify(
                        this.screenItems
                            .filter((item) => item.type === TYPE_DATAFIELD)
                            .map((item) => ({
                                datafieldId: item.datafieldId,
                                top: item.top,
                                left: item.left,
                                showCheckbox: item.showCheckbox,
                                fontSize: item.fontSize,
                                fontStyle: item.fontStyle,
                                color: item.color,
                            }))
                    ),
                    sawlines: JSON.stringify(
                        this.screenItems
                            .filter((item) => item.type === TYPE_SAWLINE)
                            .map((item) => ({
                                sawlineId: item.sawlineId,
                                top: item.top,
                                left: item.left,
                                showCheckbox: item.showCheckbox,
                                fontSize: item.fontSize,
                                fontStyle: item.fontStyle,
                                color: item.color,
                            }))
                    ),
                    special: JSON.stringify(
                        this.screenItems
                            .filter((item) => item.type === TYPE_SPECIAL)
                            .map((item) => ({
                                key: item.key,
                                top: item.top,
                                left: item.left,
                                showCheckbox: item.showCheckbox,
                                fontSize: item.fontSize,
                                fontStyle: item.fontStyle,
                                color: item.color,
                            }))
                    ),
                };

                this.saving = true;
                workstationScreenApi
                    .updateWorkstationScreen(payload)
                    .then(() => {
                        showInfoMessage(
                            this.trans(
                                'pages.workstation_screen_form.workstation_screen_updated'
                            )
                        );
                        this.$router.push('/settingsworkstationtypes');
                    })
                    .catch((error) => {
                        const errorCode = ErrorHelper.parseError(
                            error.response.data
                        );
                        showErrorMessage(this.translateErrorCode(errorCode));
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            }
        },
        fetchProductTypes() {
            if (!this.workstationId) {
                this.producttypes = [];
                return null;
            }

            return productTypeApi
                .fetchAvailableForWorkstation(this.workstationId)
                .then((response) => {
                    this.producttypes = response.data.data;

                    // Add edit product type
                    if (
                        this.formType === 'edit' &&
                        this.producttypeLabel !== null
                    ) {
                        this.producttypes.push({
                            producttypeId: this.producttypeId,
                            label: this.producttypeLabel,
                        });
                    }

                    // Set product type id to null if it does not exist in the list
                    const producttype = this.producttypes.find(
                        (pt) => pt.producttypeId === this.producttypeId
                    );
                    this.producttypeId = producttype
                        ? producttype.producttypeId
                        : null;

                    return Promise.all([
                        this.fetchDatafields(),
                        this.fetchSawlines(),
                    ]);
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_screen_form.unknown_error_fetching_product_types'
                        )
                    );
                });
        },
        fetchDatafields() {
            if (!this.producttypeId) {
                this.datafields = [];
                return null;
            }

            return productTypeApi
                .fetchDataFields(this.producttypeId)
                .then((response) => {
                    this.datafields = response.data.data;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_screen_form.unknown_error_fetching_datafields'
                        )
                    );
                });
        },
        fetchSawlines() {
            if (!this.producttypeId) {
                this.sawlines = [];
                return null;
            }

            return productTypeApi
                .fetchSawlines(this.producttypeId)
                .then((response) => {
                    this.sawlines = response.data.data;
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.workstation_screen_form.unknown_error_fetching_sawlines'
                        )
                    );
                });
        },
        getErrorFromValidator(validator) {
            if (!validator || !validator.$invalid) {
                return false;
            }

            if (!validator.$dirty) {
                return false;
            }

            if (validator.required === false) {
                return this.trans(
                    'pages.workstation_screen_form.required_field'
                );
            }

            return false;
        },
    },
    components: {
        WorkstationScreenEditor,
        Loader,
        AutoCompleteInput,
        Spinner,
    },
};
</script>
